
import {defineComponent} from 'vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import ValueFormatter from '@/utils/value-formatter'
import ReportService from "@/services/report-service";
import ReportFilterView from '@/components/ReportFilterView.vue'
import ReportChartView from '@/components/ReportChartView.vue'
import ReportTableView from '@/components/ReportTableView.vue'
import {ReportChart, ReportChartOption, ReportData, ReportFilterType, ReportTable, ReportTemplate} from '@/model/models'

export default defineComponent({
  name: 'ReportView',
  components: {
    PulseLoader,
    ReportFilterView,
    ReportChartView,
    ReportTableView
  },
  props: {
    reportId: String
  },
  data () {
    return {
      reportTemplate: new ReportTemplate({}),
      reportData: [] as ReportData[],
      filters: new Map(),
      isLoading: false
    }
  },
  created() {
    const query = this.$route.query

    for (const key in query) {
      this.filters.set(key, query[key])
    }

    this.loadTemplate()
  },
  methods: {
    onFilterChanged(data: any) {
      if (data.value && data.value.length) {
        this.filters.set(data.name, data.value)
      } else {
        this.filters.delete(data.name)
      }

      this.loadData()
    },
    loadTemplate() {
      const id = this.reportId || ''
      if (id.length) {
        ReportService.getReport(id).then((report: ReportTemplate) => {
          this.reportTemplate = report
          if (this.reportTemplate.filters[0] === undefined){
            this.loadData()
          }
        }).catch((error) => {
          console.log(error)
        })
      }
    },
    loadData() {
      const id = this.reportId || ''
      if (id.length) {
        this.isLoading = true

        ReportService.getData(id, this.filterParams).then((data: ReportData[]) => {
          this.reportData = data
          this.isLoading = false
        }).catch((error) => {
          console.log(error)
          this.isLoading = false
        })
      }
    },
    tableDataSet(table: ReportTable) : ReportData | null {
      if (this.reportData) {
        const dataSets = this.reportData.filter(data => data.name === table.dataset)
        if (dataSets.length > 0) {
          return dataSets[0]
        }
      }

      return null
    },
    chartDataSet(chart: ReportChart) : ReportData | null {
      if (this.reportData) {
        const dataSets = this.reportData.filter(data => data.name === chart.dataset)
        if (dataSets.length > 0) {
          if (chart.options.indexOf(ReportChartOption.DailyTimeline) >= 0) {
            return this.normalizedDailyDataSet(dataSets[0], chart.axis.value)
          } else if (chart.options.indexOf(ReportChartOption.HourlyTimeline) >= 0) {
            return this.normalizedHourlyDataSet(dataSets[0], chart.axis.value)
          } else {
            return dataSets[0]
          }
        }
      }

      return null
    },
    normalizedDailyDataSet(data: ReportData, valueName: string) {
      const timeRange = this.filterTimeRange
      const columnIndex = data.columns.indexOf(valueName)
      const columCount = data.columns.length

      if (timeRange && columnIndex >= 0) {
        var newValues: number[][] = []
        var startDate = timeRange.start * 1000
        const maxDate = timeRange.end * 1000

        data.values.forEach(row => {
          const date = ValueFormatter.getDate(row[columnIndex])?.getTime() || 0

          while (startDate < date) {
            var newRow = Array.from({length: columCount}).map(_ => 0)
            newRow[columnIndex] = startDate
            newValues.push(newRow)

            startDate += 86400000
          }

          row[columnIndex] = date
          newValues.push(row)

          startDate += 86400000
        })

        while (startDate < maxDate) {
          var newRow = Array.from({length: columCount}).map(_ => 0)
          newRow[columnIndex] = startDate
          newValues.push(newRow)

          startDate += 86400000
        }

        const newData = new ReportData({})
        newData.name = data.name
        newData.columns = data.columns
        newData.values = newValues

        return newData
      }

      return data
    },
    normalizedHourlyDataSet(data: ReportData, valueName: string) {
      const columnIndex = data.columns.indexOf(valueName)
      const columCount = data.columns.length

      if (columnIndex >= 0) {
        var newValues: number[][] = []

        const hours = data.values.map(row => ValueFormatter.getDate(row[columnIndex])?.getHours() || -1).filter(h => h >= 0)
        const minHour = Math.min(...hours)
        const maxHour = Math.max(...hours)

        const today = new Date()
        var startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), minHour < 8 ? minHour : 8, 0, 0,0).getTime()
        const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), maxHour > 18 ? maxHour : 18, 59, 59,0).getTime()

        data.values.forEach(row => {
          const date = ValueFormatter.getDate(row[columnIndex])?.getTime() || 0

          while (startDate < date) {
            var newRow = Array.from({length: columCount}).map(_ => 0)
            newRow[columnIndex] = startDate
            newValues.push(newRow)

            startDate += 3600000
          }

          row[columnIndex] = date
          newValues.push(row)

          startDate += 3600000
        })

        while (startDate < maxDate) {
          var newRow = Array.from({length: columCount}).map(_ => 0)
          newRow[columnIndex] = startDate
          newValues.push(newRow)

          startDate += 3600000
        }

        const newData = new ReportData({})
        newData.name = data.name
        newData.columns = data.columns
        newData.values = newValues

        return newData
      }

      return data
    },
    chartStyle(chart: ReportChart) {
      return window.innerHeight >= 580 ? chart.style : ''
    }
  },
  computed: {
    hasData() : boolean {
      if (this.reportData) {
        return this.reportData.map(d => d.values.length).filter(v => v === 0).length === 0
      }

      return false
    },
    filterTimeRange() : { start: number, end: number } | null {
      if (this.reportTemplate) {
        const filters = this.reportTemplate!.filters.filter(f => f.type === ReportFilterType.TimeRange)
        if (filters.length) {
          const values = this.filters.get(filters[0].name).split(',') as string[]
          if (values.length > 1) {
            return { start: +values[0], end: +values[1] }
          }
        }
      }

      return null
    },
    filterParams() {
      var params: string[] = []

      for (let entry of this.filters.entries()) {
        params.push(entry[0] + '=' + entry[1])
      }
      return params.join('&')
    }
  }
})
